import React from 'react';
import { useTranslation } from 'react-i18next';

const About = () => {
  const { t } = useTranslation();

  return (
    <div className="max-w-[1200px] mx-auto px-4 md:px-8">
      {/* Hero Section */}
      <section className="text-center py-16 md:py-24 px-4 md:px-8 bg-gradient-to-br from-gray-100 to-white -mx-4 md:-mx-8 -mt-8">
        <h1 className="text-4xl md:text-[3.5rem] font-bold text-[#1d1d1f] mb-4 tracking-tight">
          {t('about.hero.title')}
        </h1>
        <p className="text-lg md:text-2xl text-gray-500 max-w-[600px] mx-auto leading-relaxed">
          {t('about.hero.subtitle')}
        </p>
      </section>

      {/* Vision Section */}
      <section className="py-12 md:py-20 border-b border-gray-200">
        <div className="max-w-[800px] mx-auto text-center">
          <h2 className="text-2xl md:text-[2rem] font-semibold text-[#1d1d1f] mb-6">
            {t('about.vision.title')}
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            {t('about.vision.description')}
          </p>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-12 md:py-20 border-b border-gray-200 bg-gray-50 -mx-4 md:-mx-8 px-4 md:px-8">
        <h2 className="text-2xl md:text-[2rem] font-semibold text-[#1d1d1f] mb-6 text-center">
          {t('about.values.title')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-[repeat(auto-fit,minmax(280px,1fr))] gap-8 mt-12 max-w-[1000px] mx-auto">
          <div className="bg-white rounded-2xl py-10 px-8 text-center shadow-[0_4px_20px_rgba(0,0,0,0.06)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(0,0,0,0.1)]">
            <div className="text-5xl mb-4">💡</div>
            <h3 className="text-xl font-semibold text-[#1d1d1f] mb-3">
              {t('about.values.innovation.title')}
            </h3>
            <p className="text-base text-gray-500 leading-relaxed">
              {t('about.values.innovation.description')}
            </p>
          </div>
          <div className="bg-white rounded-2xl py-10 px-8 text-center shadow-[0_4px_20px_rgba(0,0,0,0.06)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(0,0,0,0.1)]">
            <div className="text-5xl mb-4">🤝</div>
            <h3 className="text-xl font-semibold text-[#1d1d1f] mb-3">
              {t('about.values.connection.title')}
            </h3>
            <p className="text-base text-gray-500 leading-relaxed">
              {t('about.values.connection.description')}
            </p>
          </div>
          <div className="bg-white rounded-2xl py-10 px-8 text-center shadow-[0_4px_20px_rgba(0,0,0,0.06)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(0,0,0,0.1)]">
            <div className="text-5xl mb-4">🌱</div>
            <h3 className="text-xl font-semibold text-[#1d1d1f] mb-3">
              {t('about.values.legacy.title')}
            </h3>
            <p className="text-base text-gray-500 leading-relaxed">
              {t('about.values.legacy.description')}
            </p>
          </div>
        </div>
      </section>

      {/* Ventures Section */}
      <section className="py-12 md:py-20 border-b border-gray-200">
        <h2 className="text-2xl md:text-[2rem] font-semibold text-[#1d1d1f] mb-6 text-center">
          {t('about.ventures.title')}
        </h2>
        <p className="text-center text-lg text-gray-500 max-w-[700px] mx-auto mb-12 leading-relaxed">
          {t('about.ventures.description')}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-[repeat(auto-fit,minmax(300px,1fr))] gap-6">
          <div className="bg-white border border-gray-200 rounded-2xl p-8 relative transition-all duration-300 hover:border-[#1d1d1f] hover:shadow-[0_4px_20px_rgba(0,0,0,0.08)]">
            <div className="inline-block py-1 px-3 bg-[#1d1d1f] text-white text-xs font-medium rounded mb-4">
              {t('about.ventures.flagship')}
            </div>
            <h3 className="text-2xl font-semibold text-[#1d1d1f] mb-3">AI Storyboard</h3>
            <p className="text-base text-gray-500 leading-relaxed">
              {t('about.ventures.aiStoryboard')}
            </p>
          </div>
          <div className="bg-white border border-gray-200 rounded-2xl p-8 relative transition-all duration-300 opacity-70 hover:border-[#1d1d1f] hover:shadow-[0_4px_20px_rgba(0,0,0,0.08)]">
            <div className="inline-block py-1 px-3 bg-gray-500 text-white text-xs font-medium rounded mb-4">
              {t('about.ventures.comingSoon')}
            </div>
            <h3 className="text-2xl font-semibold text-[#1d1d1f] mb-3">Creative Studio</h3>
            <p className="text-base text-gray-500 leading-relaxed">
              {t('about.ventures.creativeStudio')}
            </p>
          </div>
          <div className="bg-white border border-gray-200 rounded-2xl p-8 relative transition-all duration-300 opacity-70 hover:border-[#1d1d1f] hover:shadow-[0_4px_20px_rgba(0,0,0,0.08)]">
            <div className="inline-block py-1 px-3 bg-gray-500 text-white text-xs font-medium rounded mb-4">
              {t('about.ventures.comingSoon')}
            </div>
            <h3 className="text-2xl font-semibold text-[#1d1d1f] mb-3">Digital Archive</h3>
            <p className="text-base text-gray-500 leading-relaxed">
              {t('about.ventures.digitalArchive')}
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-12 md:py-20 text-center">
        <h2 className="text-2xl md:text-[2rem] font-semibold text-[#1d1d1f] mb-6 text-center">
          {t('about.contact.title')}
        </h2>
        <p className="text-lg text-gray-500 mb-8">
          {t('about.contact.description')}
        </p>
        <div className="flex flex-col md:flex-row justify-center gap-6 md:gap-12">
          <div className="flex flex-col gap-2">
            <span className="text-sm text-gray-400 uppercase tracking-wide">
              {t('about.contact.email')}
            </span>
            <span className="text-lg text-[#1d1d1f] font-medium">
              contact@ilouli.com
            </span>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
